import React, { useState, useEffect, useCallback, useMemo } from 'react';
import api from '../api';
import Layout from './Layout';

// ============================================
// API FUNCTIONS
// ============================================

const fetchProgramTypes = async (tenantId) => {
  const response = await api.get('/api/program-types', { 
    params: { tenantId, status: 'active' } 
  });
  return response.data.programTypes || [];
};

const fetchPrograms = async (tenantId, programTypeId) => {
  const response = await api.get('/api/programs', { 
    params: { tenantId, programTypeId, status: 'active' } 
  });
  return response.data.programs || [];
};

const fetchAcademicYears = async (tenantId) => {
  const [activeRes, completedRes] = await Promise.all([
    api.get('/api/academic-year/all-active', { params: { tenantId } }),
    api.get('/api/academic-year/all-completed', { params: { tenantId } })
  ]);
  
  const activeYears = activeRes.data.academicYears || activeRes.data.activeAcademicYears || [];
  const completedYears = completedRes.data.academicYears || [];
  return [...activeYears, ...completedYears];
};

const fetchSemesters = async (academicYearId) => {
  const response = await api.get(`/api/classManager/semesters/${academicYearId}`);
  return response.data.semesters || [];
};

const fetchTerms = async (tenantId, academicYearId) => {
  const response = await api.get(`/api/academic-year/${academicYearId}/terms`, { 
    params: { tenantId } 
  });
  return response.data.terms || [];
};

const fetchClasses = async (tenantId, filters) => {
  const response = await api.get('/api/head-of-institution/class-details/list', {
    params: { ...filters, tenantId }
  });
  return response.data;
};

const fetchClassDetails = async (tenantId, classId) => {
  const response = await api.get('/api/head-of-institution/class-details/details', {
    params: { classId, tenantId }
  });
  return response.data;
};

// ============================================
// STUDENT DETAILS MODAL COMPONENT (Full Extended UI)
// ============================================
const StudentDetailsModal = ({ student, onClose }) => {
  const [profileImageUrl, setProfileImageUrl] = useState(null);
  const [imageLoading, setImageLoading] = useState(true);

  useEffect(() => {
    const getProfilePhotoUrl = async () => {
      if (!student) {
        setImageLoading(false);
        return;
      }

      if (!student.profilePhoto && !student.profilePhotoUrl) {
        setImageLoading(false);
        return;
      }

      try {
        let photoUrl = null;
        
        if (student.profilePhoto?.url) {
          photoUrl = student.profilePhoto.url;
        } else if (student.profilePhotoUrl) {
          photoUrl = student.profilePhotoUrl;
        } else if (typeof student.profilePhoto === 'string') {
          photoUrl = student.profilePhoto;
        }

        if (!photoUrl) {
          setImageLoading(false);
          return;
        }

        if (photoUrl.startsWith('http://') || photoUrl.startsWith('https://')) {
          setProfileImageUrl(photoUrl);
          setImageLoading(false);
          return;
        }

        const baseURL = api.defaults.baseURL || '';
        const fullUrl = `${baseURL}${photoUrl}`;
        
        try {
          await api.head(fullUrl);
          setProfileImageUrl(fullUrl);
        } catch {
          setProfileImageUrl(null);
        }
      } catch (error) {
        console.error('Error loading profile photo:', error);
        setProfileImageUrl(null);
      } finally {
        setImageLoading(false);
      }
    };

    getProfilePhotoUrl();
  }, [student]);

  if (!student) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" onClick={onClose}>
      <div className="relative w-full max-w-2xl bg-white rounded-2xl shadow-2xl mx-4 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-10 text-white hover:text-gray-200 transition-colors bg-black/20 hover:bg-black/30 rounded-full p-2 backdrop-blur-sm"
        >
          <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        {/* Header with Gradient */}
        <div className="bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 px-8 py-6 rounded-t-2xl relative overflow-hidden">
          <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/3" />
          <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/5 rounded-full translate-y-1/2 -translate-x-1/4" />
          
          <div className="relative flex items-center gap-5">
            {imageLoading ? (
              <div className="h-20 w-20 rounded-full bg-white/20 animate-pulse border-2 border-white/30 flex-shrink-0" />
            ) : profileImageUrl ? (
              <img 
                src={profileImageUrl} 
                alt={student.fullName}
                className="h-20 w-20 rounded-full object-cover border-3 border-white/40 shadow-lg flex-shrink-0"
                onError={(e) => {
                  e.target.style.display = 'none';
                  setProfileImageUrl(null);
                }}
              />
            ) : (
              <div className="h-20 w-20 rounded-full bg-white/20 flex items-center justify-center text-3xl font-bold text-white border-3 border-white/40 shadow-lg flex-shrink-0">
                {student.fullName?.charAt(0) || '?'}
              </div>
            )}
            <div className="flex-1 min-w-0">
              <h3 className="text-2xl font-bold text-white truncate">{student.fullName}</h3>
              <p className="text-blue-100 text-sm truncate">{student.email}</p>
              {student.usn && (
                <span className="inline-block mt-1.5 bg-white/20 backdrop-blur-sm text-white text-xs font-mono px-3 py-0.5 rounded-full border border-white/20">
                  {student.usn}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Body */}
        <div className="p-8 space-y-6">
          
          {/* Quick Stats Row */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="bg-gradient-to-br from-blue-50 to-blue-100/50 rounded-xl p-4 text-center border border-blue-200/50 min-w-0">
              <p className="text-xs font-semibold uppercase tracking-wider text-blue-600">USN</p>
              <p className="text-sm font-bold text-blue-900 mt-1 font-mono break-all" title={student.usn || student.rollNumber || 'N/A'}>
                {student.usn || student.rollNumber || 'N/A'}
              </p>
            </div>
            <div className="bg-gradient-to-br from-emerald-50 to-emerald-100/50 rounded-xl p-4 text-center border border-emerald-200/50 min-w-0">
              <p className="text-xs font-semibold uppercase tracking-wider text-emerald-600">Status</p>
              <span className={`inline-block mt-1 px-3 py-0.5 text-xs font-bold uppercase tracking-wide rounded-full ${
                student.isActive || student.status === 'active' 
                  ? 'bg-emerald-500 text-white' 
                  : 'bg-rose-500 text-white'
              }`}>
                {student.status || (student.isActive ? 'Active' : 'Inactive')}
              </span>
            </div>
            <div className="bg-gradient-to-br from-purple-50 to-purple-100/50 rounded-xl p-4 text-center border border-purple-200/50 min-w-0">
              <p className="text-xs font-semibold uppercase tracking-wider text-purple-600">Class</p>
              <p className="text-sm font-bold text-purple-900 mt-1 truncate w-full overflow-hidden text-ellipsis" title={student.className || 'N/A'}>
                {student.className || 'N/A'}
              </p>
            </div>
            <div className="bg-gradient-to-br from-amber-50 to-amber-100/50 rounded-xl p-4 text-center border border-amber-200/50 min-w-0">
              <p className="text-xs font-semibold uppercase tracking-wider text-amber-600">Admission</p>
              <p className="text-sm font-bold text-amber-900 mt-1 truncate w-full overflow-hidden text-ellipsis" title={student.admissionNumber || 'N/A'}>
                {student.admissionNumber || 'N/A'}
              </p>
            </div>
          </div>

          {/* Contact Information */}
          <div>
            <h4 className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-3 flex items-center gap-2">
              <span className="w-1 h-4 bg-emerald-500 rounded-full"></span>
              Contact Information
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="bg-gradient-to-br from-slate-50 to-slate-100/50 rounded-xl p-4 border border-slate-200/80">
                <div className="flex items-center gap-2">
                  <svg className="h-4 w-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  <p className="text-xs text-slate-500 font-medium">Email</p>
                </div>
                <p className="text-sm font-semibold text-slate-800 mt-1 ml-6 truncate">{student.email || 'N/A'}</p>
              </div>
              <div className="bg-gradient-to-br from-slate-50 to-slate-100/50 rounded-xl p-4 border border-slate-200/80">
                <div className="flex items-center gap-2">
                  <svg className="h-4 w-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                  </svg>
                  <p className="text-xs text-slate-500 font-medium">Phone</p>
                </div>
                <p className="text-sm font-semibold text-slate-800 mt-1 ml-6">{student.studentPhoneNumber || student.phoneNumber || 'N/A'}</p>
              </div>
              {student.parentName && (
                <div className="bg-gradient-to-br from-slate-50 to-slate-100/50 rounded-xl p-4 border border-slate-200/80">
                  <div className="flex items-center gap-2">
                    <svg className="h-4 w-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                    </svg>
                    <p className="text-xs text-slate-500 font-medium">Parent</p>
                  </div>
                  <p className="text-sm font-semibold text-slate-800 mt-1 ml-6">{student.parentName}</p>
                </div>
              )}
              {student.parentEmail && (
                <div className="bg-gradient-to-br from-slate-50 to-slate-100/50 rounded-xl p-4 border border-slate-200/80">
                  <div className="flex items-center gap-2">
                    <svg className="h-4 w-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                    <p className="text-xs text-slate-500 font-medium">Parent Email</p>
                  </div>
                  <p className="text-sm font-semibold text-slate-800 mt-1 ml-6 truncate">{student.parentEmail}</p>
                </div>
              )}
              {student.parentPhoneNumber && (
                <div className="bg-gradient-to-br from-slate-50 to-slate-100/50 rounded-xl p-4 border border-slate-200/80">
                  <div className="flex items-center gap-2">
                    <svg className="h-4 w-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                    </svg>
                    <p className="text-xs text-slate-500 font-medium">Parent Phone</p>
                  </div>
                  <p className="text-sm font-semibold text-slate-800 mt-1 ml-6">{student.parentPhoneNumber}</p>
                </div>
              )}
            </div>
          </div>

          {/* Personal Information */}
          {(student.dateOfBirth || student.gender || student.bloodGroup || student.nationality || student.category) && (
            <div>
              <h4 className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-3 flex items-center gap-2">
                <span className="w-1 h-4 bg-purple-500 rounded-full"></span>
                Personal Information
              </h4>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {student.dateOfBirth && (
                  <div className="bg-gradient-to-br from-purple-50 to-purple-100/50 rounded-xl p-4 text-center border border-purple-200/50">
                    <p className="text-xs font-semibold uppercase tracking-wider text-purple-600">DOB</p>
                    <p className="text-sm font-bold text-purple-900 mt-1">{student.dateOfBirth}</p>
                  </div>
                )}
                {student.gender && (
                  <div className="bg-gradient-to-br from-purple-50 to-purple-100/50 rounded-xl p-4 text-center border border-purple-200/50">
                    <p className="text-xs font-semibold uppercase tracking-wider text-purple-600">Gender</p>
                    <p className="text-sm font-bold text-purple-900 mt-1 capitalize">{student.gender}</p>
                  </div>
                )}
                {student.bloodGroup && (
                  <div className="bg-gradient-to-br from-purple-50 to-purple-100/50 rounded-xl p-4 text-center border border-purple-200/50">
                    <p className="text-xs font-semibold uppercase tracking-wider text-purple-600">Blood Group</p>
                    <p className="text-sm font-bold text-purple-900 mt-1">{student.bloodGroup}</p>
                  </div>
                )}
                {student.nationality && (
                  <div className="bg-gradient-to-br from-purple-50 to-purple-100/50 rounded-xl p-4 text-center border border-purple-200/50">
                    <p className="text-xs font-semibold uppercase tracking-wider text-purple-600">Nationality</p>
                    <p className="text-sm font-bold text-purple-900 mt-1">{student.nationality}</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Address */}
          {student.address && (
            <div>
              <h4 className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-3 flex items-center gap-2">
                <span className="w-1 h-4 bg-amber-500 rounded-full"></span>
                Address
              </h4>
              <div className="bg-gradient-to-br from-amber-50 to-amber-100/50 rounded-xl p-4 border border-amber-200/50">
                <div className="flex items-start gap-3">
                  <svg className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  <p className="text-sm text-slate-700">{student.address}</p>
                </div>
              </div>
            </div>
          )}

          {/* Footer */}
          <div className="border-t border-slate-200 pt-4 mt-2">
            <div className="flex items-center justify-between text-xs text-slate-400">
              <span>Admission: {student.admissionDate ? new Date(student.admissionDate).toLocaleDateString() : 'N/A'}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ============================================
// LOADING COMPONENT
// ============================================
const ClassesLoading = () => (
  <div className="w-full rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden">
    <div className="border-b border-slate-200 bg-slate-900 px-5 py-4">
      <div className="flex items-center gap-3">
        <div className="h-8 w-8 rounded bg-white/20 animate-pulse" />
        <div className="space-y-2 flex-1">
          <div className="h-4 w-32 rounded bg-white/20 animate-pulse" />
          <div className="h-3 w-48 rounded bg-white/10 animate-pulse" />
        </div>
      </div>
    </div>
    <div className="p-5 space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="space-y-2">
            <div className="h-3 w-20 rounded bg-slate-200 animate-pulse" />
            <div className="h-10 rounded-lg bg-slate-100 animate-pulse" />
          </div>
        ))}
      </div>
    </div>
  </div>
);

// ============================================
// MAIN COMPONENT
// ============================================
const Classes = () => {
  
  // State for selections
  const [tenantId, setTenantId] = useState('');
  const [programTypeId, setProgramTypeId] = useState('');
  const [programId, setProgramId] = useState('');
  const [academicYearId, setAcademicYearId] = useState('');
  const [semesterId, setSemesterId] = useState('');
  const [termId, setTermId] = useState('');
  const [isSemesterBased, setIsSemesterBased] = useState(null);
  
  // State for dropdown options
  const [programTypes, setProgramTypes] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [academicYears, setAcademicYears] = useState([]);
  const [semesters, setSemesters] = useState([]);
  const [terms, setTerms] = useState([]);
  
  // State for classes
  const [classes, setClasses] = useState([]);
  const [selectedClass, setSelectedClass] = useState(null);
  const [classDetails, setClassDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [error, setError] = useState('');
  const [contextSummary, setContextSummary] = useState(null);

  // Student Modal State
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Student Table Pagination State
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // Reset page when selected class details change
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedClass]);

  // Load tenantId from localStorage
  useEffect(() => {
    const tid = localStorage.getItem('tenantId');
    if (tid) setTenantId(tid);
  }, []);

  // Helper to check if academic year is completed
  const isAcademicYearCompleted = useCallback((academicYear) => {
    return academicYear?.status === 'completed' || 
           academicYear?.isCompleted === true ||
           academicYear?.academicYearStatus === 'completed';
  }, []);

  // ============================================
  // FETCH DROPDOWN DATA
  // ============================================
  
  useEffect(() => {
    if (!tenantId) return;
    fetchProgramTypes(tenantId)
      .then(setProgramTypes)
      .catch(() => setError('Failed to load program types'));
  }, [tenantId]);

  useEffect(() => {
    if (!tenantId || !programTypeId) {
      setPrograms([]);
      return;
    }
    fetchPrograms(tenantId, programTypeId)
      .then(setPrograms)
      .catch(() => setError('Failed to load programs'));
  }, [tenantId, programTypeId]);

  useEffect(() => {
    if (!tenantId) return;
    fetchAcademicYears(tenantId)
      .then(setAcademicYears)
      .catch(() => setError('Failed to load academic years'));
  }, [tenantId]);

  useEffect(() => {
    if (!academicYearId) {
      setSemesters([]);
      setTerms([]);
      setIsSemesterBased(null);
      return;
    }

    const selectedAY = academicYears.find(y => y._id === academicYearId);
    if (selectedAY && isAcademicYearCompleted(selectedAY)) {
      setError('This academic year is completed. View only mode.');
    } else {
      setError('');
    }

    fetchSemesters(academicYearId)
      .then(sems => {
        if (sems.length > 0) {
          setSemesters(sems);
          setIsSemesterBased(true);
          setTerms([]);
        } else {
          fetchTerms(tenantId, academicYearId)
            .then(trms => {
              setTerms(trms);
              setIsSemesterBased(false);
              setSemesters([]);
            })
            .catch(() => setError('Failed to load terms'));
        }
      })
      .catch(() => {
        fetchTerms(tenantId, academicYearId)
          .then(trms => {
            setTerms(trms);
            setIsSemesterBased(false);
            setSemesters([]);
          })
          .catch(() => setError('Failed to load semesters or terms'));
      });
  }, [academicYearId, tenantId, academicYears, isAcademicYearCompleted]);

  // ============================================
  // FETCH CLASSES WHEN CONTEXT CHANGES
  // ============================================
  useEffect(() => {
    if (!tenantId || !programTypeId || !programId || !academicYearId) {
      setClasses([]);
      setSelectedClass(null);
      setClassDetails(null);
      return;
    }

    const hasPeriod = (isSemesterBased === true && semesterId) || (isSemesterBased === false && termId);
    if (!hasPeriod) {
      setClasses([]);
      setSelectedClass(null);
      setClassDetails(null);
      return;
    }

    const filters = {
      programTypeId,
      programId,
      academicYearId,
    };

    if (isSemesterBased === true && semesterId) {
      filters.semesterId = semesterId;
    } else if (isSemesterBased === false && termId) {
      filters.termId = termId;
    }

    setLoading(true);
    setError('');

    const selectedPT = programTypes.find(pt => pt._id === programTypeId);
    const selectedP = programs.find(p => p._id === programId);
    const fontAY = academicYears.find(y => y._id === academicYearId);
    const selectedSem = semesters.find(s => s._id === semesterId);
    const selectedTerm = terms.find(t => t._id === termId);

    setContextSummary({
      programTypeName: selectedPT?.name || '',
      programName: selectedP?.name || '',
      programCode: selectedP?.code || '',
      academicYearName: fontAY?.name || fontAY?.academicYear || '',
      periodName: isSemesterBased 
        ? (selectedSem?.name || selectedSem?.semesterLabel || `Semester ${selectedSem?.semesterNumber}` || '')
        : (selectedTerm?.name || selectedTerm?.termLabel || `Term ${selectedTerm?.termNumber}` || ''),
      periodType: isSemesterBased ? 'Semester' : 'Term',
      isCompleted: isAcademicYearCompleted(fontAY)
    });

    fetchClasses(tenantId, filters)
      .then(response => {
        const classList = response.data?.classes || [];
        setClasses(classList);
        setSelectedClass(null);
        setClassDetails(null);
        setLoading(false);
      })
      .catch(err => {
        setError(err.response?.data?.message || 'Failed to load classes');
        setLoading(false);
      });
  }, [
    tenantId, 
    programTypeId, 
    programId, 
    academicYearId, 
    semesterId, 
    termId, 
    isSemesterBased,
    programTypes,
    programs,
    academicYears,
    semesters,
    terms,
    isAcademicYearCompleted
  ]);

  // ============================================
  // FETCH CLASS DETAILS
  // ============================================
  const handleClassSelect = (classId) => {
    setSelectedClass(classId);
    setLoadingDetails(true);
    setError('');

    fetchClassDetails(tenantId, classId)
      .then(response => {
        const details = response.data?.classes?.[0] || null;
        setClassDetails(details);
        setLoadingDetails(false);
      })
      .catch(err => {
        setError(err.response?.data?.message || 'Failed to load class details');
        setLoadingDetails(false);
      });
  };

  // ============================================
  // STUDENT MODAL HANDLERS
  // ============================================
  const handleViewStudent = (student) => {
    setSelectedStudent(student);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedStudent(null);
  };

  // ============================================
  // SORTED AND PAGINATED STUDENTS MEMO
  // ============================================
  const processedStudents = useMemo(() => {
    if (!classDetails?.students) return { items: [], totalPages: 0, totalCount: 0, startIndex: 0 };
    
    const sorted = [...classDetails.students].sort((a, b) => {
      const usnA = (a.usn || a.rollNumber || '').toString().toLowerCase();
      const usnB = (b.usn || b.rollNumber || '').toString().toLowerCase();
      return usnA.localeCompare(usnB, undefined, { numeric: true, sensitivity: 'base' });
    });

    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = sorted.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(sorted.length / itemsPerPage);

    return {
      items: currentItems,
      totalPages,
      totalCount: sorted.length,
      startIndex: indexOfFirstItem
    };
  }, [classDetails?.students, currentPage]);

  // ============================================
  // HANDLERS
  // ============================================
  const handleProgramTypeChange = (value) => {
    setProgramTypeId(value);
    setProgramId('');
    setAcademicYearId('');
    setSemesterId('');
    setTermId('');
    setIsSemesterBased(null);
    setClasses([]);
    setSelectedClass(null);
    setClassDetails(null);
  };

  const handleProgramChange = (value) => {
    setProgramId(value);
    setAcademicYearId('');
    setSemesterId('');
    setTermId('');
    setIsSemesterBased(null);
    setClasses([]);
    setSelectedClass(null);
    setClassDetails(null);
  };

  const handleAcademicYearChange = (value) => {
    setAcademicYearId(value);
    setSemesterId('');
    setTermId('');
    setIsSemesterBased(null);
    setClasses([]);
    setSelectedClass(null);
    setClassDetails(null);
  };

  const handleSemesterChange = (value) => {
    setSemesterId(value);
    setTermId('');
    setClasses([]);
    setSelectedClass(null);
    setClassDetails(null);
  };

  const handleTermChange = (value) => {
    setTermId(value);
    setSemesterId('');
    setClasses([]);
    setSelectedClass(null);
    setClassDetails(null);
  };

  // ============================================
  // GET LABELS
  // ============================================
  const getAcademicYearLabel = (ayId) => {
    const ay = academicYears.find(y => y._id === ayId);
    if (!ay) return '';
    const isCompleted = isAcademicYearCompleted(ay);
    return `${ay.name || ay.academicYear}${isCompleted ? ' (Completed)' : ''}`;
  };

  const getSemesterLabel = (semId) => {
    const sem = semesters.find(s => s._id === semId);
    if (!sem) return '';
    const isCompleted = sem?.status === 'completed';
    return `${sem.name || sem.semesterLabel || `Semester ${sem.semesterNumber}`}${isCompleted ? ' (Completed)' : ''}`;
  };

  const getTermLabel = (tId) => {
    const term = terms.find(t => t._id === tId);
    if (!term) return '';
    const isCompleted = term?.status === 'completed';
    return `${term.name || term.termLabel || `Term ${term.termNumber}`}${isCompleted ? ' (Completed)' : ''}`;
  };

  // ============================================
  // CLASSES CONTENT COMPONENT
  // ============================================
  const ClassesContent = () => {
    if (!tenantId) {
      return <ClassesLoading />;
    }

    const isContextSelected = programTypeId && programId && academicYearId && 
      ((isSemesterBased === true && semesterId) || (isSemesterBased === false && termId));

    return (
      <div className="p-4 md:p-6 bg-slate-50 min-h-screen font-sans text-slate-900 selection:bg-blue-500/10">
        
        {/* Main Page Header */}
        <div className="mb-6">
          <h1 className="text-xl font-bold tracking-tight text-slate-900 md:text-2xl">Class Management</h1>
          <p className="text-xs md:text-sm text-slate-500 mt-1">Select your program options below to view lists of classes and schedules.</p>
        </div>

        {/* ============================================ */}
        {/* FILTERS AND OPTIONS BOX */}
        {/* ============================================ */}
        <div className="w-full rounded-xl border border-slate-200 bg-white shadow-sm mb-6 overflow-hidden">
          <div className="bg-slate-900 border-b border-slate-800 px-5 py-4">
            <div className="flex items-center gap-3">
              <div className="flex h-8 w-8 items-center justify-center rounded bg-blue-600/10 text-blue-400">
                <svg className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 14l9-5-9-5-9 5 9 5z" />
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 14l6.16-3.422A12.083 12.083 0 0112 21.5c-4.418 0-8-1.79-8-4V9" />
                </svg>
              </div>
              <div>
                <h2 className="text-sm font-semibold text-white">Select Academic Options</h2>
                <p className="text-xs text-slate-400">Choose a program, academic year, and semester or term.</p>
              </div>
            </div>
          </div>

          <div className="p-5">
            {/* Error Message */}
            {error && (
              <div className="mb-4 flex items-start gap-2.5 rounded-lg border border-amber-200 bg-amber-50/50 px-4 py-3">
                <svg className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
                <span className="text-xs font-medium text-amber-800">{error}</span>
              </div>
            )}

            {/* Select Dropdowns Grid */}
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
              
              {/* Program Type Select */}
              <div className="space-y-1">
                <label className="text-[11px] font-bold uppercase tracking-wider text-slate-500">
                  Program Type <span className="text-red-500">*</span>
                </label>
                <select
                  value={programTypeId}
                  onChange={(e) => handleProgramTypeChange(e.target.value)}
                  className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-medium focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none"
                >
                  <option value="">Select program type</option>
                  {programTypes.map(pt => (
                    <option key={pt._id} value={pt._id}>{pt.name}</option>
                  ))}
                </select>
              </div>

              {/* Program Select */}
              <div className="space-y-1">
                <label className="text-[11px] font-bold uppercase tracking-wider text-slate-500">
                  Program <span className="text-red-500">*</span>
                </label>
                <select
                  value={programId}
                  onChange={(e) => handleProgramChange(e.target.value)}
                  disabled={!programTypeId}
                  className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-medium focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none disabled:bg-slate-50 disabled:text-slate-400"
                >
                  <option value="">Select program</option>
                  {programs.map(p => (
                    <option key={p._id} value={p._id}>{p.name} ({p.code})</option>
                  ))}
                </select>
              </div>

              {/* Academic Year Select */}
              <div className="space-y-1">
                <label className="text-[11px] font-bold uppercase tracking-wider text-slate-500">
                  Academic Year <span className="text-red-500">*</span>
                </label>
                <select
                  value={academicYearId}
                  onChange={(e) => handleAcademicYearChange(e.target.value)}
                  disabled={!programId}
                  className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-medium focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none disabled:bg-slate-50 disabled:text-slate-400"
                >
                  <option value="">Select academic year</option>
                  {academicYears.map(ay => (
                    <option key={ay._id} value={ay._id}>
                      {getAcademicYearLabel(ay._id)}
                    </option>
                  ))}
                </select>
              </div>

              {/* Semester Select */}
              {isSemesterBased === true && (
                <div className="space-y-1">
                  <label className="text-[11px] font-bold uppercase tracking-wider text-slate-500">
                    Semester <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={semesterId}
                    onChange={(e) => handleSemesterChange(e.target.value)}
                    disabled={!academicYearId || semesters.length === 0}
                    className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-medium focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none disabled:bg-slate-50 disabled:text-slate-400"
                  >
                    <option value="">Select semester</option>
                    {semesters.map(sem => (
                      <option key={sem._id} value={sem._id}>
                        {getSemesterLabel(sem._id)}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* Term Select */}
              {isSemesterBased === false && (
                <div className="space-y-1">
                  <label className="text-[11px] font-bold uppercase tracking-wider text-slate-500">
                    Term <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={termId}
                    onChange={(e) => handleTermChange(e.target.value)}
                    disabled={!academicYearId || terms.length === 0}
                    className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-medium focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none disabled:bg-slate-50 disabled:text-slate-400"
                  >
                    <option value="">Select term</option>
                    {terms.map(term => (
                      <option key={term._id} value={term._id}>
                        {getTermLabel(term._id)}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>

            {/* Selected Summary Text Box */}
            {contextSummary && isContextSelected && (
              <div className="mt-5 border-t border-slate-100 pt-4">
                <div className="rounded-lg border border-slate-200 bg-slate-50/50 p-4">
                  <h3 className="mb-3 text-[10px] font-bold uppercase tracking-wider text-slate-400">
                    Your Current Selection
                  </h3>
                  <div className="grid grid-cols-2 gap-4 text-xs sm:grid-cols-4">
                    <div>
                      <span className="text-slate-400 block mb-0.5">Program Type</span>
                      <p className="font-semibold text-slate-800">{contextSummary.programTypeName}</p>
                    </div>
                    <div>
                      <span className="text-slate-400 block mb-0.5">Program</span>
                      <p className="font-semibold text-slate-800">{contextSummary.programName} ({contextSummary.programCode})</p>
                    </div>
                    <div>
                      <span className="text-slate-400 block mb-0.5">Academic Year</span>
                      <p className="font-semibold text-slate-800">{contextSummary.academicYearName}</p>
                    </div>
                    <div>
                      <span className="text-slate-400 block mb-0.5">{contextSummary.periodType}</span>
                      <p className="font-semibold text-slate-800">{contextSummary.periodName}</p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* ============================================ */}
        {/* CLASSES LIST SECTION */}
        {/* ============================================ */}
        {isContextSelected && (
          <div className="mt-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-bold uppercase tracking-wider text-slate-500">
                Classes Found 
                {loading ? (
                  <span className="ml-2 text-xs font-normal text-slate-400 lowercase italic">loading classes...</span>
                ) : (
                  <span className="ml-2 rounded-full bg-slate-200 px-2 py-0.5 text-xs font-semibold text-slate-700">
                    {classes.length} total
                  </span>
                )}
              </h3>
            </div>

            {loading ? (
              <div className="flex justify-center py-10">
                <div className="h-6 w-6 animate-spin rounded-full border-2 border-slate-400 border-t-transparent" />
              </div>
            ) : classes.length === 0 ? (
              <div className="rounded-xl border border-slate-200 bg-white p-10 text-center">
                <p className="text-xs font-medium text-slate-400">No classes found for the selected items.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {classes.map((cls) => (
                  <div
                    key={cls.classId}
                    onClick={() => handleClassSelect(cls.classId)}
                    className={`cursor-pointer rounded-xl border p-4 transition-all duration-150 ${
                      selectedClass === cls.classId
                        ? 'border-blue-600 bg-blue-50/30 shadow-sm'
                        : 'border-slate-200 bg-white hover:border-slate-300 hover:shadow-sm'
                    }`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <h4 className="font-bold text-slate-900 text-sm tracking-tight">{cls.className}</h4>
                      <span className="shrink-0 rounded bg-slate-100 px-2 py-0.5 text-[10px] font-bold text-slate-600 uppercase tracking-wide">
                        {cls.subjectCount || 0} subjects
                      </span>
                    </div>
                    <div className="mt-3 space-y-1.5 text-xs text-slate-600 border-t border-dashed border-slate-100 pt-2.5">
                      <div className="flex items-center gap-1.5">
                        <span className="text-slate-400 font-medium">Program:</span>
                        <span className="text-slate-800 font-medium">{cls.programName} ({cls.programCode})</span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <span className="text-slate-400 font-medium">Year:</span>
                        <span>{cls.academicYearName}</span>
                      </div>
                      {(cls.termName || cls.semesterName) && (
                        <div className="flex items-center gap-1.5">
                          <span className="text-slate-400 font-medium">Period:</span>
                          <span>{cls.termName || cls.semesterName}</span>
                        </div>
                      )}
                      <div className="flex items-center justify-between mt-2 pt-1">
                        <p className="text-[11px] font-semibold text-slate-500">{cls.studentCount || 0} students</p>
                        <span className={`inline-block rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${
                          cls.status === 'active' ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-600'
                        }`}>
                          {cls.status || 'active'}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ============================================ */}
        {/* DETAILED VIEW SECTION */}
        {/* ============================================ */}
        {selectedClass && (
          <div className="mt-8 border-t border-slate-200 pt-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-bold uppercase tracking-wider text-slate-500">
                Class Details Box
                {loadingDetails && <span className="ml-2 text-xs font-normal text-slate-400 lowercase italic">loading info...</span>}
              </h3>
              <button
                onClick={() => {
                  setSelectedClass(null);
                  setClassDetails(null);
                }}
                className="text-xs font-bold uppercase tracking-wider text-slate-400 hover:text-slate-900 transition"
              >
                Close Details
              </button>
            </div>

            {loadingDetails ? (
              <div className="flex justify-center py-10">
                <div className="h-6 w-6 animate-spin rounded-full border-2 border-slate-400 border-t-transparent" />
              </div>
            ) : classDetails ? (
              <div className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden">
                
                {/* Header Box of Selected Class */}
                <div className="bg-slate-50 border-b border-slate-200 p-5">
                  <div className="flex items-center justify-between flex-wrap gap-3">
                    <div>
                      <h4 className="text-lg font-bold text-slate-900 tracking-tight">{classDetails.className}</h4>
                      <div className="mt-1.5 flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-500 font-medium">
                        <span>Program: <strong className="text-slate-700">{classDetails.programName} ({classDetails.programCode})</strong></span>
                        <span>Year: <strong className="text-slate-700">{classDetails.academicYearName}</strong></span>
                        {classDetails.termName && <span>Term: <strong className="text-slate-700">{classDetails.termName}</strong></span>}
                        {classDetails.semesterName && <span>Semester: <strong className="text-slate-700">{classDetails.semesterName}</strong></span>}
                        <span>Total Enrolled: <strong className="text-slate-700">{classDetails.studentCount} students</strong></span>
                      </div>
                    </div>
                    <span className={`rounded px-2.5 py-1 text-xs font-bold uppercase tracking-wider ${
                      classDetails.status === 'active' ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-slate-100 text-slate-600'
                    }`}>
                      {classDetails.status}
                    </span>
                  </div>
                </div>

                <div className="p-5 space-y-6">
                  
                  {/* Subject List */}
                  <div>
                    <h5 className="mb-3 text-xs font-bold uppercase tracking-wider text-slate-400">Subjects</h5>
                    {classDetails.subjects && classDetails.subjects.length > 0 ? (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {classDetails.subjects.map((sub, idx) => (
                          <div key={idx} className="rounded-lg border border-slate-200 p-3 bg-white hover:border-slate-300 transition">
                            <div className="flex items-center justify-between gap-2">
                              <span className="font-semibold text-slate-900 text-xs">{sub.subjectName}</span>
                              <div className="flex gap-1 shrink-0">
                                {sub.isLab && <span className="rounded bg-blue-50 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-blue-700 border border-blue-100">Lab</span>}
                                {sub.isLanguage && <span className="rounded bg-emerald-50 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-emerald-700 border border-emerald-100">Language</span>}
                                {sub.isElective && <span className="rounded bg-purple-50 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-purple-700 border border-purple-100">Elective</span>}
                              </div>
                            </div>
                            <div className="mt-2 text-xs text-slate-500 flex flex-col gap-0.5">
                              {sub.departmentName && <span>Department: <strong className="text-slate-700 font-medium">{sub.departmentName}</strong></span>}
                              {sub.faculty && (
                                <span>Teacher: <strong className="text-slate-700 font-medium">{sub.faculty.fullName}</strong></span>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-xs italic text-slate-400">No subjects found.</p>
                    )}
                  </div>

                  {/* Faculty List */}
                  {classDetails.facultySummary && classDetails.facultySummary.length > 0 && (
                    <div>
                      <h5 className="mb-3 text-xs font-bold uppercase tracking-wider text-slate-400">Teachers</h5>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {classDetails.facultySummary.map((fac, idx) => (
                          <div key={idx} className="rounded-lg border border-slate-200 p-3 bg-white">
                            <p className="font-bold text-slate-900 text-xs">{fac.fullName}</p>
                            <p className="text-xs text-slate-400 font-mono mt-0.5">{fac.email}</p>
                            <div className="mt-2.5 flex flex-wrap gap-1">
                              {fac.subjects.map((s, i) => (
                                <span key={i} className="rounded bg-slate-100 px-2 py-0.5 text-[10px] font-medium text-slate-700">
                                  {s.subjectName}
                                </span>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Students Directory List (Sorted & Paginated) */}
                  <div>
                    <h5 className="mb-3 text-xs font-bold uppercase tracking-wider text-slate-400">
                      Student Roster ({processedStudents.totalCount || 0})
                    </h5>
                    {processedStudents.items.length > 0 ? (
                      <div className="space-y-4">
                        <div className="overflow-x-auto border border-slate-200 rounded-lg">
                          <table className="w-full text-left border-collapse text-xs">
                            <thead>
                              <tr className="bg-slate-50 border-b border-slate-200 text-slate-500 font-bold uppercase tracking-wider">
                                <th className="px-4 py-3 font-bold text-[10px] w-16">#</th>
                                <th className="px-4 py-3 font-bold text-[10px]">USN</th>
                                <th className="px-4 py-3 font-bold text-[10px]">Full Name</th>
                                <th className="px-4 py-3 font-bold text-[10px]">Email</th>
                                <th className="px-4 py-3 font-bold text-[10px] text-center">Status</th>
                                <th className="px-4 py-3 font-bold text-[10px] text-center">Action</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                              {processedStudents.items.map((student, idx) => (
                                <tr key={idx} className="hover:bg-slate-50/50 transition">
                                  <td className="px-4 py-2.5 text-slate-400 font-medium">
                                    {processedStudents.startIndex + idx + 1}
                                  </td>
                                  <td className="px-4 py-2.5 font-mono font-semibold text-slate-700 uppercase">
                                    {student.usn || student.rollNumber || 'N/A'}
                                  </td>
                                  <td className="px-4 py-2.5 font-semibold text-slate-900">
                                    <div className="flex items-center gap-2.5">
                                      {(() => {
                                        let photoUrl = null;
                                        if (student.profilePhoto?.url) {
                                          photoUrl = student.profilePhoto.url;
                                        } else if (student.profilePhotoUrl) {
                                          photoUrl = student.profilePhotoUrl;
                                        } else if (typeof student.profilePhoto === 'string') {
                                          photoUrl = student.profilePhoto;
                                        }
                                        
                                        if (photoUrl && !photoUrl.startsWith('http://') && !photoUrl.startsWith('https://')) {
                                          const baseURL = api.defaults.baseURL || '';
                                          photoUrl = `${baseURL}${photoUrl}`;
                                        }
                                        
                                        return photoUrl ? (
                                          <img 
                                            src={photoUrl} 
                                            alt=""
                                            className="h-5 w-5 rounded-full object-cover shrink-0 ring-1 ring-slate-200"
                                            onError={(e) => {
                                              e.target.style.display = 'none';
                                              e.target.parentElement.innerHTML = `<div class="h-5 w-5 rounded-full bg-slate-100 flex items-center justify-center text-[10px] font-bold text-slate-500 shrink-0 border border-slate-200">${student.fullName?.charAt(0) || '?'}</div>`;
                                            }}
                                          />
                                        ) : (
                                          <div className="h-5 w-5 rounded-full bg-slate-100 flex items-center justify-center text-[10px] font-bold text-slate-500 shrink-0 border border-slate-200">
                                            {student.fullName?.charAt(0) || '?'}
                                          </div>
                                        );
                                      })()}
                                      {student.fullName}
                                    </div>
                                  </td>
                                  <td className="px-4 py-2.5 text-slate-500 font-mono">{student.email}</td>
                                  <td className="px-4 py-2.5 text-center">
                                    <span className={`inline-block rounded px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide ${
                                      student.isActive || student.status === 'active' ? 'bg-emerald-50 text-emerald-700' : 'bg-rose-50 text-rose-700'
                                    }`}>
                                      {student.status || (student.isActive ? 'active' : 'inactive')}
                                    </span>
                                  </td>
                                  <td className="px-4 py-2.5 text-center">
                                    <button
                                      onClick={() => handleViewStudent(student)}
                                      className="inline-flex items-center gap-1 rounded bg-blue-50 px-2.5 py-1 text-xs font-medium text-blue-700 hover:bg-blue-100 transition-colors"
                                    >
                                      <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                      </svg>
                                      View
                                    </button>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>

                        {/* Pagination Controls */}
                        {processedStudents.totalPages > 1 && (
                          <div className="flex items-center justify-between border-t border-slate-200 bg-white px-2 py-3 sm:px-4">
                            <div className="flex flex-1 justify-between sm:hidden">
                              <button
                                disabled={currentPage === 1}
                                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                                className="relative inline-flex items-center rounded-md border border-slate-300 bg-white px-4 py-2 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                              >
                                Previous
                              </button>
                              <button
                                disabled={currentPage === processedStudents.totalPages}
                                onClick={() => setCurrentPage(prev => Math.min(prev + 1, processedStudents.totalPages))}
                                className="relative ml-3 inline-flex items-center rounded-md border border-slate-300 bg-white px-4 py-2 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                              >
                                Next
                              </button>
                            </div>
                            <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
                              <div>
                                <p className="text-xs text-slate-500">
                                  Showing <span className="font-semibold text-slate-800">{processedStudents.startIndex + 1}</span> to{' '}
                                  <span className="font-semibold text-slate-800">
                                    {Math.min(processedStudents.startIndex + itemsPerPage, processedStudents.totalCount)}
                                  </span>{' '}
                                  of <span className="font-semibold text-slate-800">{processedStudents.totalCount}</span> entries
                                </p>
                              </div>
                              <div>
                                <nav className="isolate inline-flex -space-x-px rounded-md shadow-sm" aria-label="Pagination">
                                  <button
                                    disabled={currentPage === 1}
                                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                                    className="relative inline-flex items-center rounded-l-md border border-slate-300 bg-white px-2 py-2 text-xs font-medium text-slate-500 hover:bg-slate-50 disabled:opacity-40"
                                  >
                                    <span className="sr-only">Previous</span>
                                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                                    </svg>
                                  </button>
                                  {[...Array(Math.min(processedStudents.totalPages, 10))].map((_, index) => {
                                    const pageNumber = index + 1;
                                    const isCurrent = currentPage === pageNumber;
                                    return (
                                      <button
                                        key={pageNumber}
                                        onClick={() => setCurrentPage(pageNumber)}
                                        className={`relative inline-flex items-center border px-3 py-1.5 text-xs font-medium focus:z-20 ${
                                          isCurrent
                                            ? 'z-10 bg-blue-600 border-blue-600 text-white'
                                            : 'bg-white border-slate-300 text-slate-600 hover:bg-slate-50'
                                        }`}
                                      >
                                        {pageNumber}
                                      </button>
                                    );
                                  })}
                                  <button
                                    disabled={currentPage === processedStudents.totalPages}
                                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, processedStudents.totalPages))}
                                    className="relative inline-flex items-center rounded-r-md border border-slate-300 bg-white px-2 py-2 text-xs font-medium text-slate-500 hover:bg-slate-50 disabled:opacity-40"
                                  >
                                    <span className="sr-only">Next</span>
                                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                    </svg>
                                  </button>
                                </nav>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    ) : (
                      <p className="text-xs italic text-slate-400">No students are currently enrolled in this class.</p>
                    )}
                  </div>
                </div>

              </div>
            ) : null}
          </div>
        )}

        {/* ============================================ */}
        {/* STUDENT DETAILS MODAL */}
        {/* ============================================ */}
        {isModalOpen && selectedStudent && (
          <StudentDetailsModal 
            student={selectedStudent} 
            onClose={handleCloseModal} 
          />
        )}
      </div>
    );
  };

  // Wrap everything in Layout
  return (
    <Layout>
      <ClassesContent />
    </Layout>
  );
};

export default Classes;